import {useEffect} from 'react';
import Markdown from 'react-markdown';
import {HighlightText} from '@components';
import {
    useFetchArticleTitlesQuery,
    useFetchCollectionNamesQuery,
} from '@api';
import {
    transformProblemLinks,
    transformAtUserLinks,
    transformSenseisLinks,
    transformInfoPageLinks,
    setArticleTitles,
    transformGoBoardThumbnails,
    transformRelativeGoBoardThumbnails,
    createRelativeGoBoardLinkTransform,
    transformCollectionLinks,
    setCollectionNames,
} from '@components';

type RelativeDiagramLink = {
    href: string;
    label: string;
};

export const MarkdownContent = ({
    text,
    preserveLineBreaks = false,
    relativeDiagrams = true,
    relativeDiagramLink,
}: {
    text: string;
    preserveLineBreaks?: boolean;
    relativeDiagrams?: boolean;
    relativeDiagramLink?: RelativeDiagramLink;
}) => {
    const hasArticleLinks = /\[info:[a-z0-9_-]+\]/i.test(text);
    const hasCollectionLinks = /\[c#\d+\]/.test(text);
    const {data: articleTitles} = useFetchArticleTitlesQuery(undefined, {
        skip: !hasArticleLinks,
    });
    const {data: collectionNames} = useFetchCollectionNamesQuery(undefined, {
        skip: !hasCollectionLinks,
    });

    useEffect(() => {
        if (articleTitles) {
            setArticleTitles(articleTitles);
        }
    }, [articleTitles]);

    useEffect(() => {
        if (collectionNames) {
            setCollectionNames(collectionNames);
        }
    }, [collectionNames]);

    const paragraphClassName = preserveLineBreaks
        ? 'whitespace-pre-line'
        : undefined;

    return (
        <Markdown
            components={{
                a: ({...props}) => <a className="link" {...props} />,
                p: ({children}) => {
                    if (typeof children === 'string') {
                        return (
                            <p className={paragraphClassName}>
                                <HighlightText text={children} />
                            </p>
                        );
                    }
                    return <p className={paragraphClassName}>{children}</p>;
                },
                h1: ({children}) => (
                    <h1 className="text-lg font-semibold my-1">{children}</h1>
                ),
                h2: ({children}) => (
                    <h2 className="text-base font-semibold my-1">{children}</h2>
                ),
                h3: ({children}) => (
                    <h3 className="text-sm font-semibold my-1">{children}</h3>
                ),
                h4: ({children}) => (
                    <h4 className="text-xs font-semibold my-1">{children}</h4>
                ),
                h5: ({children}) => (
                    <h5 className="text-xs font-medium my-1">{children}</h5>
                ),
                h6: ({children}) => (
                    <h6 className="text-xs font-medium my-1">{children}</h6>
                ),
                code: ({children}) => (
                    <code className="font-mono text-xs px-1 py-0.5 rounded">
                        {children}
                    </code>
                ),
                blockquote: ({children}) => (
                    <blockquote className="border-l-2 border-neutral-300 pl-4 italic text-neutral-600 my-2">
                        {children}
                    </blockquote>
                ),
                ul: ({children}) => (
                    <ul className="list-disc pl-5 my-1">{children}</ul>
                ),
                ol: ({children}) => (
                    <ol className="list-decimal pl-5 my-1">{children}</ol>
                ),
                img: ({...props}) => (
                    <img className="max-w-full h-auto" {...props} />
                ),
            }}
            remarkPlugins={[
                transformProblemLinks,
                transformAtUserLinks,
                transformSenseisLinks,
                [transformInfoPageLinks, articleTitles],
                transformGoBoardThumbnails,
                ...(relativeDiagrams
                    ? [transformRelativeGoBoardThumbnails]
                    : relativeDiagramLink
                      ? [
                            createRelativeGoBoardLinkTransform(
                                relativeDiagramLink
                            ),
                        ]
                      : []),
                [transformCollectionLinks, collectionNames],
            ]}>
            {text}
        </Markdown>
    );
};
